import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';
import { ArrowLeft, FlaskConical, Info, Users, FileText, BarChart3, Copy, Check, Sparkles, Loader2, Clock } from 'lucide-react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Methodology data for quantitative research
const QUANTITATIVE_METHODOLOGY = {
  ontology: {
    value: "Reality is single and objective",
    explanation: "There is one true reality that exists independently of human perception and can be discovered through research."
  },
  epistemology: {
    value: "Reality can be measured",
    explanation: "Knowledge is gained through objective measurement, observation, and empirical evidence."
  },
  philosophy: {
    value: "Positivism",
    explanation: "Scientific method is the best way to uncover truth. Emphasizes observable, measurable facts and statistical analysis."
  },
  approach: {
    value: "Quantitative",
    explanation: "Uses numerical data, statistical analysis, and structured methods to test hypotheses and establish patterns."
  },
  strategy: {
    options: ["Survey Research", "Experimental Design", "Cross-sectional Study", "Longitudinal Study"],
    explanation: "Survey collects data at one point; Experimental tests cause-effect; Cross-sectional compares groups; Longitudinal tracks over time."
  },
  sampling: {
    type: "Probability Sampling",
    techniques: ["Simple Random", "Systematic", "Stratified", "Cluster"],
    explanation: "Every member of population has known chance of selection. Enables generalization to larger population.",
    sampleSize: "Minimum 110 respondents recommended for statistical validity. Use sample size calculator for precise calculation."
  },
  collection: {
    methods: ["Structured Questionnaire", "Survey Instrument", "Standardized Tests"],
    explanation: "Structured instruments with closed-ended questions, Likert scales, and predetermined response options."
  },
  analysis: {
    types: ["Descriptive Statistics", "Inferential Statistics"],
    techniques: ["Mean, Median, Mode", "Standard Deviation", "T-tests", "ANOVA", "Regression", "Correlation", "Chi-square"],
    explanation: "Descriptive summarizes data; Inferential tests hypotheses and determines relationships between variables."
  }
};

// Methodology data for qualitative research
const QUALITATIVE_METHODOLOGY = {
  ontology: {
    value: "Reality is multiple and subjective",
    explanation: "Multiple realities exist based on individual perspectives, experiences, and social contexts."
  },
  epistemology: {
    value: "Reality is socially constructed",
    explanation: "Knowledge is created through social interactions, interpretations, and understanding of meanings."
  },
  philosophy: {
    value: "Interpretivism",
    explanation: "Understanding comes from interpreting the meanings people attach to their experiences and social world."
  },
  approach: {
    value: "Qualitative",
    explanation: "Explores phenomena in depth using non-numerical data, focusing on understanding meanings and contexts."
  },
  strategy: {
    options: ["Case Study", "Narrative Research", "Phenomenology", "Ethnography", "Grounded Theory"],
    explanation: "Case Study examines specific instance; Narrative explores stories; Phenomenology studies lived experiences."
  },
  sampling: {
    type: "Non-Probability Sampling",
    techniques: ["Purposive", "Judgmental", "Quota", "Snowball", "Convenience"],
    explanation: "Participants selected based on specific criteria or characteristics relevant to the research question.",
    sampleSize: "Typically 8-20 participants recommended. Focus on depth of information rather than generalizability."
  },
  collection: {
    methods: ["In-depth Interviews", "Focus Groups", "Observation", "Document Analysis"],
    explanation: "Open-ended methods that allow participants to express views in their own words and context."
  },
  analysis: {
    types: ["Thematic Analysis", "Content Analysis", "Discourse Analysis"],
    techniques: ["Coding", "Theme Identification", "Pattern Recognition", "Narrative Analysis"],
    explanation: "Identifies patterns, themes, and meanings in textual data through systematic coding and interpretation."
  }
};

const ResearchMethodology = () => {
  const { projectId } = useParams();
  const navigate = useNavigate();
  const { token } = useAuth();
  
  const [project, setProject] = useState(null);
  const [loading, setLoading] = useState(true);
  const [copied, setCopied] = useState(false);
  // Quantitative selections
  const [selectedQuantStrategy, setSelectedQuantStrategy] = useState('');
  const [selectedQuantSamplingTechnique, setSelectedQuantSamplingTechnique] = useState('');
  const [selectedQuantCollection, setSelectedQuantCollection] = useState('');
  // Qualitative selections
  const [selectedQualStrategy, setSelectedQualStrategy] = useState('');
  const [selectedQualSamplingTechnique, setSelectedQualSamplingTechnique] = useState('');
  const [selectedQualCollection, setSelectedQualCollection] = useState('');
  
  const [generating, setGenerating] = useState(false);
  const [savedOutline, setSavedOutline] = useState(null);

  useEffect(() => {
    const fetchProject = async () => {
      try {
        const res = await axios.get(`${API}/projects/${projectId}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setProject(res.data);
        // Load saved methodology if exists
        if (res.data.methodology_outline) {
          setSavedOutline(res.data.methodology_outline);
          if (res.data.methodology_selections) {
            const selections = res.data.methodology_selections;
            setSelectedQuantStrategy(selections.quant_strategy || '');
            setSelectedQuantSamplingTechnique(selections.quant_sampling_technique || '');
            setSelectedQuantCollection(selections.quant_collection || '');
            setSelectedQualStrategy(selections.qual_strategy || '');
            setSelectedQualSamplingTechnique(selections.qual_sampling_technique || '');
            setSelectedQualCollection(selections.qual_collection || '');
          }
        }
      } catch (err) {
        toast.error('Failed to load project');
        navigate('/dashboard');
      } finally {
        setLoading(false);
      }
    };
    fetchProject();
  }, [projectId, token, navigate]);

  const isQuantitative = project?.research_type === 'quantitative';
  const isQualitative = project?.research_type === 'qualitative';
  const isMixed = project?.research_type === 'mixed';
  
  // For mixed methods, we show both. For single type, show the appropriate one
  const showQuantitative = isQuantitative || isMixed;
  const showQualitative = isQualitative || isMixed;

  const handleGenerateOutline = async () => {
    setGenerating(true);
    try {
      // Build the outline text
      const outlineText = generateOutlineText();
      
      // Save to database
      await axios.patch(`${API}/projects/${projectId}/methodology`, {
        outline: outlineText,
        selections: {
          quant_strategy: selectedQuantStrategy,
          quant_sampling_technique: selectedQuantSamplingTechnique,
          quant_collection: selectedQuantCollection,
          qual_strategy: selectedQualStrategy,
          qual_sampling_technique: selectedQualSamplingTechnique,
          qual_collection: selectedQualCollection
        }
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setSavedOutline(outlineText);
      toast.success('Research methodology outline saved!');
    } catch (err) {
      toast.error('Failed to save outline');
    } finally {
      setGenerating(false);
    }
  };

  const generateOutlineText = () => {
    let text = `RESEARCH DESIGN & METHODOLOGY OUTLINE
=====================================

Research Type: ${isMixed ? 'Mixed Methods' : isQuantitative ? 'Quantitative' : 'Qualitative'}
`;

    if (showQuantitative) {
      const m = QUANTITATIVE_METHODOLOGY;
      text += `
${'='.repeat(50)}
QUANTITATIVE COMPONENT
${'='.repeat(50)}

PHILOSOPHICAL FOUNDATIONS
-------------------------
Ontology: ${m.ontology.value}
Epistemology: ${m.epistemology.value}
Research Philosophy: ${m.philosophy.value}
Research Approach: ${m.approach.value}

RESEARCH STRATEGY
-----------------
Selected: ${selectedQuantStrategy || 'Not selected'}
Options: ${m.strategy.options.join(', ')}

RESEARCH METHODOLOGY
--------------------
1. SAMPLING
   Type: ${m.sampling.type}
   Selected Technique: ${selectedQuantSamplingTechnique || 'Not selected'}
   Available Techniques: ${m.sampling.techniques.join(', ')}
   Recommended Sample Size: ${m.sampling.sampleSize}

2. DATA COLLECTION
   Selected Method: ${selectedQuantCollection || 'Not selected'}
   Available Methods: ${m.collection.methods.join(', ')}

3. DATA ANALYSIS
   Types: ${m.analysis.types.join(', ')}
   Techniques: ${m.analysis.techniques.join(', ')}
`;
    }

    if (showQualitative) {
      const m = QUALITATIVE_METHODOLOGY;
      text += `
${'='.repeat(50)}
QUALITATIVE COMPONENT
${'='.repeat(50)}

PHILOSOPHICAL FOUNDATIONS
-------------------------
Ontology: ${m.ontology.value}
Epistemology: ${m.epistemology.value}
Research Philosophy: ${m.philosophy.value}
Research Approach: ${m.approach.value}

RESEARCH STRATEGY
-----------------
Selected: ${selectedQualStrategy || 'Not selected'}
Options: ${m.strategy.options.join(', ')}

RESEARCH METHODOLOGY
--------------------
1. SAMPLING
   Type: ${m.sampling.type}
   Selected Technique: ${selectedQualSamplingTechnique || 'Not selected'}
   Available Techniques: ${m.sampling.techniques.join(', ')}
   Recommended Sample Size: ${m.sampling.sampleSize}

2. DATA COLLECTION
   Selected Method: ${selectedQualCollection || 'Not selected'}
   Available Methods: ${m.collection.methods.join(', ')}

3. DATA ANALYSIS
   Types: ${m.analysis.types.join(', ')}
   Techniques: ${m.analysis.techniques.join(', ')}
`;
    }

    return text;
  };

  const handleCopy = () => {
    const text = savedOutline || generateOutlineText();
    navigator.clipboard.writeText(text);
    setCopied(true);
    toast.success('Copied to clipboard!');
    setTimeout(() => setCopied(false), 2000);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="animate-spin w-8 h-8 border-4 border-indigo-600 border-t-transparent rounded-full"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <header className="bg-white border-b border-slate-200 sticky top-0 z-10">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-4">
              <button 
                onClick={() => navigate(`/project/${projectId}`)} 
                className="text-slate-500 hover:text-slate-700"
              >
                <ArrowLeft className="w-5 h-5" />
              </button>
              <h1 className="text-lg font-semibold text-slate-900">Research Design & Methodology</h1>
            </div>
            <div className="flex items-center gap-2">
              <Button variant="outline" size="sm" onClick={handleCopy}>
                {copied ? <><Check className="w-4 h-4 mr-1" /> Copied</> : <><Copy className="w-4 h-4 mr-1" /> Copy</>}
              </Button>
              <Button 
                size="sm" 
                onClick={handleGenerateOutline}
                disabled={generating}
                className="bg-indigo-600 hover:bg-indigo-700"
              >
                {generating ? (
                  <><Loader2 className="w-4 h-4 mr-1 animate-spin" /> Saving...</>
                ) : (
                  <><Sparkles className="w-4 h-4 mr-1" /> Save Outline</>
                )}
              </Button>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Research Type Indicator */}
        <div className="bg-white rounded-xl border border-slate-200 p-4 mb-6 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className={`p-2 rounded-lg ${isMixed ? 'bg-purple-100' : isQuantitative ? 'bg-indigo-100' : 'bg-rose-100'}`}>
              <FlaskConical className={`w-5 h-5 ${isMixed ? 'text-purple-600' : isQuantitative ? 'text-indigo-600' : 'text-rose-600'}`} />
            </div>
            <div>
              <p className="font-medium text-slate-900">
                {isMixed ? 'Mixed Methods' : isQuantitative ? 'Quantitative' : 'Qualitative'} Research Design
              </p>
              <p className="text-sm text-slate-500">{project?.title}</p>
            </div>
          </div>
          {savedOutline && (
            <div className="flex items-center gap-2 text-sm text-green-600">
              <Check className="w-4 h-4" />
              Outline Saved
            </div>
          )}
        </div>

        {isMixed && (
          <div className="bg-purple-50 border border-purple-200 rounded-xl p-4 mb-6">
            <p className="text-purple-800 text-sm">
              <strong>Mixed Methods Research:</strong> Your study combines both quantitative and qualitative approaches. 
              Both methodology frameworks are shown below. Complete selections for each component as applicable to your research design.
            </p>
          </div>
        )}

        {/* Quantitative Methodology Table */}
        {showQuantitative && (
          <>
            <div className="flex items-center gap-3 mb-4">
              <div className="p-2 bg-indigo-100 rounded-lg">
                <BarChart3 className="w-5 h-5 text-indigo-600" />
              </div>
              <h2 className="text-xl font-semibold text-slate-900">Quantitative Research Design</h2>
            </div>
            
            <div className="bg-white rounded-xl border border-slate-200 overflow-hidden mb-6">
              <table className="w-full">
                <thead>
                  <tr className="bg-indigo-50 border-b border-slate-200">
                    <th className="px-6 py-4 text-left text-sm font-semibold text-slate-900 w-1/4">Component</th>
                    <th className="px-6 py-4 text-left text-sm font-semibold text-slate-900 w-1/4">Value</th>
                    <th className="px-6 py-4 text-left text-sm font-semibold text-slate-900">Explanation</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  <tr>
                    <td className="px-6 py-4 font-medium text-slate-900">Ontology</td>
                    <td className="px-6 py-4 text-indigo-700 font-medium">{QUANTITATIVE_METHODOLOGY.ontology.value}</td>
                    <td className="px-6 py-4 text-sm text-slate-600">{QUANTITATIVE_METHODOLOGY.ontology.explanation}</td>
                  </tr>
                  <tr className="bg-slate-50/50">
                    <td className="px-6 py-4 font-medium text-slate-900">Epistemology</td>
                    <td className="px-6 py-4 text-indigo-700 font-medium">{QUANTITATIVE_METHODOLOGY.epistemology.value}</td>
                    <td className="px-6 py-4 text-sm text-slate-600">{QUANTITATIVE_METHODOLOGY.epistemology.explanation}</td>
                  </tr>
                  <tr>
                    <td className="px-6 py-4 font-medium text-slate-900">Research Philosophy</td>
                    <td className="px-6 py-4 text-indigo-700 font-medium">{QUANTITATIVE_METHODOLOGY.philosophy.value}</td>
                    <td className="px-6 py-4 text-sm text-slate-600">{QUANTITATIVE_METHODOLOGY.philosophy.explanation}</td>
                  </tr>
                  <tr className="bg-slate-50/50">
                    <td className="px-6 py-4 font-medium text-slate-900">Research Approach</td>
                    <td className="px-6 py-4 text-indigo-700 font-medium">{QUANTITATIVE_METHODOLOGY.approach.value}</td>
                    <td className="px-6 py-4 text-sm text-slate-600">{QUANTITATIVE_METHODOLOGY.approach.explanation}</td>
                  </tr>
                  <tr>
                    <td className="px-6 py-4 font-medium text-slate-900">Research Strategy</td>
                    <td className="px-6 py-4">
                      <select 
                        value={selectedQuantStrategy}
                        onChange={(e) => setSelectedQuantStrategy(e.target.value)}
                        className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                      >
                        <option value="">Select strategy...</option>
                        {QUANTITATIVE_METHODOLOGY.strategy.options.map(opt => (
                          <option key={opt} value={opt}>{opt}</option>
                        ))}
                      </select>
                    </td>
                    <td className="px-6 py-4 text-sm text-slate-600">{QUANTITATIVE_METHODOLOGY.strategy.explanation}</td>
                  </tr>
                </tbody>
              </table>
            </div>

            {/* Quantitative Methodology Components */}
            <div className="bg-white rounded-xl border border-slate-200 overflow-hidden mb-8">
              <div className="bg-indigo-50 px-6 py-4 border-b border-slate-200">
                <h3 className="font-semibold text-slate-900 flex items-center gap-2">
                  <FileText className="w-5 h-5 text-indigo-600" />
                  Quantitative Methodology Components
                </h3>
              </div>
              <table className="w-full">
                <thead>
                  <tr className="bg-slate-50 border-b border-slate-200">
                    <th className="px-6 py-3 text-left text-sm font-semibold text-slate-900 w-1/4">Component</th>
                    <th className="px-6 py-3 text-left text-sm font-semibold text-slate-900 w-1/4">Selection</th>
                    <th className="px-6 py-3 text-left text-sm font-semibold text-slate-900">Details & Explanation</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  <tr>
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-2">
                        <Users className="w-5 h-5 text-indigo-600" />
                        <span className="font-medium text-slate-900">Sampling</span>
                      </div>
                      <p className="text-xs text-slate-500 mt-1">{QUANTITATIVE_METHODOLOGY.sampling.type}</p>
                    </td>
                    <td className="px-6 py-4">
                      <select 
                        value={selectedQuantSamplingTechnique}
                        onChange={(e) => setSelectedQuantSamplingTechnique(e.target.value)}
                        className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                      >
                        <option value="">Select technique...</option>
                        {QUANTITATIVE_METHODOLOGY.sampling.techniques.map(tech => (
                          <option key={tech} value={tech}>{tech}</option>
                        ))}
                      </select>
                    </td>
                    <td className="px-6 py-4">
                      <p className="text-sm text-slate-600 mb-2">{QUANTITATIVE_METHODOLOGY.sampling.explanation}</p>
                      <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-lg text-sm bg-indigo-50 text-indigo-700">
                        <Info className="w-4 h-4" />
                        <span className="font-medium">{QUANTITATIVE_METHODOLOGY.sampling.sampleSize}</span>
                      </div>
                    </td>
                  </tr>
                  <tr className="bg-slate-50/50">
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-2">
                        <FileText className="w-5 h-5 text-indigo-600" />
                        <span className="font-medium text-slate-900">Data Collection</span>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <select 
                        value={selectedQuantCollection}
                        onChange={(e) => setSelectedQuantCollection(e.target.value)}
                        className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                      >
                        <option value="">Select method...</option>
                        {QUANTITATIVE_METHODOLOGY.collection.methods.map(method => (
                          <option key={method} value={method}>{method}</option>
                        ))}
                      </select>
                    </td>
                    <td className="px-6 py-4">
                      <p className="text-sm text-slate-600">{QUANTITATIVE_METHODOLOGY.collection.explanation}</p>
                    </td>
                  </tr>
                  <tr>
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-2">
                        <BarChart3 className="w-5 h-5 text-indigo-600" />
                        <span className="font-medium text-slate-900">Data Analysis</span>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="space-y-1">
                        {QUANTITATIVE_METHODOLOGY.analysis.types.map(type => (
                          <span key={type} className="inline-block px-2 py-1 bg-indigo-100 text-indigo-700 text-xs rounded mr-1">
                            {type}
                          </span>
                        ))}
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <p className="text-sm text-slate-600 mb-2">{QUANTITATIVE_METHODOLOGY.analysis.explanation}</p>
                      <p className="text-xs text-slate-500">
                        <strong>Techniques:</strong> {QUANTITATIVE_METHODOLOGY.analysis.techniques.join(', ')}
                      </p>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </>
        )}

        {/* Qualitative Methodology Table */}
        {showQualitative && (
          <>
            <div className="flex items-center gap-3 mb-4">
              <div className="p-2 bg-rose-100 rounded-lg">
                <FileText className="w-5 h-5 text-rose-600" />
              </div>
              <h2 className="text-xl font-semibold text-slate-900">Qualitative Research Design</h2>
            </div>
            
            <div className="bg-white rounded-xl border border-slate-200 overflow-hidden mb-6">
              <table className="w-full">
                <thead>
                  <tr className="bg-rose-50 border-b border-slate-200">
                    <th className="px-6 py-4 text-left text-sm font-semibold text-slate-900 w-1/4">Component</th>
                    <th className="px-6 py-4 text-left text-sm font-semibold text-slate-900 w-1/4">Value</th>
                    <th className="px-6 py-4 text-left text-sm font-semibold text-slate-900">Explanation</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  <tr>
                    <td className="px-6 py-4 font-medium text-slate-900">Ontology</td>
                    <td className="px-6 py-4 text-rose-700 font-medium">{QUALITATIVE_METHODOLOGY.ontology.value}</td>
                    <td className="px-6 py-4 text-sm text-slate-600">{QUALITATIVE_METHODOLOGY.ontology.explanation}</td>
                  </tr>
                  <tr className="bg-slate-50/50">
                    <td className="px-6 py-4 font-medium text-slate-900">Epistemology</td>
                    <td className="px-6 py-4 text-rose-700 font-medium">{QUALITATIVE_METHODOLOGY.epistemology.value}</td>
                    <td className="px-6 py-4 text-sm text-slate-600">{QUALITATIVE_METHODOLOGY.epistemology.explanation}</td>
                  </tr>
                  <tr>
                    <td className="px-6 py-4 font-medium text-slate-900">Research Philosophy</td>
                    <td className="px-6 py-4 text-rose-700 font-medium">{QUALITATIVE_METHODOLOGY.philosophy.value}</td>
                    <td className="px-6 py-4 text-sm text-slate-600">{QUALITATIVE_METHODOLOGY.philosophy.explanation}</td>
                  </tr>
                  <tr className="bg-slate-50/50">
                    <td className="px-6 py-4 font-medium text-slate-900">Research Approach</td>
                    <td className="px-6 py-4 text-rose-700 font-medium">{QUALITATIVE_METHODOLOGY.approach.value}</td>
                    <td className="px-6 py-4 text-sm text-slate-600">{QUALITATIVE_METHODOLOGY.approach.explanation}</td>
                  </tr>
                  <tr>
                    <td className="px-6 py-4 font-medium text-slate-900">Research Strategy</td>
                    <td className="px-6 py-4">
                      <select 
                        value={selectedQualStrategy}
                        onChange={(e) => setSelectedQualStrategy(e.target.value)}
                        className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:ring-2 focus:ring-rose-500 focus:border-rose-500"
                      >
                        <option value="">Select strategy...</option>
                        {QUALITATIVE_METHODOLOGY.strategy.options.map(opt => (
                          <option key={opt} value={opt}>{opt}</option>
                        ))}
                      </select>
                    </td>
                    <td className="px-6 py-4 text-sm text-slate-600">{QUALITATIVE_METHODOLOGY.strategy.explanation}</td>
                  </tr>
                </tbody>
              </table>
            </div>

            {/* Qualitative Methodology Components */}
            <div className="bg-white rounded-xl border border-slate-200 overflow-hidden mb-8">
              <div className="bg-rose-50 px-6 py-4 border-b border-slate-200">
                <h3 className="font-semibold text-slate-900 flex items-center gap-2">
                  <FileText className="w-5 h-5 text-rose-600" />
                  Qualitative Methodology Components
                </h3>
              </div>
              <table className="w-full">
                <thead>
                  <tr className="bg-slate-50 border-b border-slate-200">
                    <th className="px-6 py-3 text-left text-sm font-semibold text-slate-900 w-1/4">Component</th>
                    <th className="px-6 py-3 text-left text-sm font-semibold text-slate-900 w-1/4">Selection</th>
                    <th className="px-6 py-3 text-left text-sm font-semibold text-slate-900">Details & Explanation</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  <tr>
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-2">
                        <Users className="w-5 h-5 text-rose-600" />
                        <span className="font-medium text-slate-900">Sampling</span>
                      </div>
                      <p className="text-xs text-slate-500 mt-1">{QUALITATIVE_METHODOLOGY.sampling.type}</p>
                    </td>
                    <td className="px-6 py-4">
                      <select 
                        value={selectedQualSamplingTechnique}
                        onChange={(e) => setSelectedQualSamplingTechnique(e.target.value)}
                        className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:ring-2 focus:ring-rose-500 focus:border-rose-500"
                      >
                        <option value="">Select technique...</option>
                        {QUALITATIVE_METHODOLOGY.sampling.techniques.map(tech => (
                          <option key={tech} value={tech}>{tech}</option>
                        ))}
                      </select>
                    </td>
                    <td className="px-6 py-4">
                      <p className="text-sm text-slate-600 mb-2">{QUALITATIVE_METHODOLOGY.sampling.explanation}</p>
                      <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-lg text-sm bg-rose-50 text-rose-700">
                        <Info className="w-4 h-4" />
                        <span className="font-medium">{QUALITATIVE_METHODOLOGY.sampling.sampleSize}</span>
                      </div>
                    </td>
                  </tr>
                  <tr className="bg-slate-50/50">
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-2">
                        <FileText className="w-5 h-5 text-rose-600" />
                        <span className="font-medium text-slate-900">Data Collection</span>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <select 
                        value={selectedQualCollection}
                        onChange={(e) => setSelectedQualCollection(e.target.value)}
                        className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:ring-2 focus:ring-rose-500 focus:border-rose-500"
                      >
                        <option value="">Select method...</option>
                        {QUALITATIVE_METHODOLOGY.collection.methods.map(method => (
                          <option key={method} value={method}>{method}</option>
                        ))}
                      </select>
                    </td>
                    <td className="px-6 py-4">
                      <p className="text-sm text-slate-600">{QUALITATIVE_METHODOLOGY.collection.explanation}</p>
                    </td>
                  </tr>
                  <tr>
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-2">
                        <BarChart3 className="w-5 h-5 text-rose-600" />
                        <span className="font-medium text-slate-900">Data Analysis</span>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="space-y-1">
                        {QUALITATIVE_METHODOLOGY.analysis.types.map(type => (
                          <span key={type} className="inline-block px-2 py-1 bg-rose-100 text-rose-700 text-xs rounded mr-1">
                            {type}
                          </span>
                        ))}
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <p className="text-sm text-slate-600 mb-2">{QUALITATIVE_METHODOLOGY.analysis.explanation}</p>
                      <p className="text-xs text-slate-500">
                        <strong>Techniques:</strong> {QUALITATIVE_METHODOLOGY.analysis.techniques.join(', ')}
                      </p>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </>
        )}

        {/* Tips Section */}
        <div className="bg-gradient-to-r from-indigo-50 to-purple-50 border border-indigo-100 rounded-xl p-6">
          <h4 className="font-semibold text-slate-900 mb-3">Research Design Tips</h4>
          <ul className="space-y-2 text-sm text-slate-700">
            <li className="flex items-start gap-2">
              <Check className="w-4 h-4 text-green-500 mt-0.5 flex-shrink-0" />
              <span>Ensure your methodology aligns with your research objectives and questions.</span>
            </li>
            {showQuantitative && (
              <li className="flex items-start gap-2">
                <Check className="w-4 h-4 text-green-500 mt-0.5 flex-shrink-0" />
                <span>For quantitative studies, aim for at least <strong>110 respondents</strong> to ensure statistical validity and enable meaningful inferential analysis.</span>
              </li>
            )}
            {showQualitative && (
              <li className="flex items-start gap-2">
                <Check className="w-4 h-4 text-green-500 mt-0.5 flex-shrink-0" />
                <span>For qualitative studies, <strong>8-20 participants</strong> typically provide data saturation. Focus on depth over breadth.</span>
              </li>
            )}
            {isMixed && (
              <li className="flex items-start gap-2">
                <Check className="w-4 h-4 text-green-500 mt-0.5 flex-shrink-0" />
                <span>For mixed methods, consider whether to use sequential (qual → quant or quant → qual) or concurrent design based on your research goals.</span>
              </li>
            )}
            <li className="flex items-start gap-2">
              <Check className="w-4 h-4 text-green-500 mt-0.5 flex-shrink-0" />
              <span>Select your strategy based on your research context, resources, and timeline.</span>
            </li>
            <li className="flex items-start gap-2">
              <Check className="w-4 h-4 text-green-500 mt-0.5 flex-shrink-0" />
              <span>Click "Save Outline" to store your methodology selections for future reference.</span>
            </li>
          </ul>
        </div>
      </main>
    </div>
  );
};

export default ResearchMethodology;
